import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { collectCustomerCodesFromRows, fetchCustomerNamesByCodes } from './demCustomerNames';
import {
  assertDemActorReady,
  fetchDemUserDisplayNameMap,
  resolveDemDisplayUser,
} from './demUserNames';
import { formatCheckedAtDisplay } from './demOrderChecked';
import type { DemOrderListFilter, DemWebOrder, DemWebOrderItem } from '../types/dem';
import { sendProcessedOrderNotifications, type ProcessedNotificationResult } from './demOrderNotifications';
import {
  formatOrderCreatorDisplay,
  formatOrderExporterDisplay,
  parseOrderBatches,
} from './demOrderBatches';
import { buildBatchAwareExportRows, finalizeOrderExport } from './demOrderExport';

const SGT = 'Asia/Singapore';
const ORDER_SELECT =
  'order_id, created_at, delivery_date, customer_code, ref_no, status, remarks, contact_person, contact_no, email, total_amount, waive_delivery, created_by, exported_by, checked_at, checked_by, items:order_items(item_code, description, qty, uom, batch_id), batches:order_batches(id, order_id, batch_no, reason, created_by, created_at, exported_by, exported_at)';

const INITIAL_PAGE = 50;
const MORE_PAGE = 100;

function parseCreatedAtDisplay(raw: string | null | undefined): string {
  if (!raw) return '—';
  try {
    const normalized = raw.includes('T') ? raw.replace('Z', '+00:00') : `${raw.replace(' ', 'T')}+00:00`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: SGT,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return raw;
  }
}

function parseDeliveryDateDisplay(raw: string | null | undefined): string {
  if (!raw) return '—';
  const datePart = String(raw).split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return raw;
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

function parseItems(raw: unknown): DemWebOrderItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      item_code: String(r.item_code ?? '').trim(),
      description: String(r.description ?? '').trim(),
      qty: Number(r.qty ?? 0),
      uom: String(r.uom ?? '').trim(),
      batch_id: r.batch_id == null ? null : String(r.batch_id).trim() || null,
    };
  });
}

function hasDeliveryFee(items: DemWebOrderItem[]): boolean {
  return items.some((item) => {
    const code = item.item_code.trim();
    const desc = item.description.trim();
    return (
      code === 'NEW PRODUCT' ||
      code === '401090' ||
      code === '601158' ||
      desc.includes('401090') ||
      desc.includes('601158') ||
      desc.includes('Delivery Charge') ||
      desc.includes('Delivery Fee')
    );
  });
}

function mapOrderRow(
  row: Record<string, unknown>,
  customerNames: Map<string, string>,
  userNames: Map<string, string>,
): DemWebOrder {
  const customerCode = String(row.customer_code ?? '').trim();
  const items = parseItems(row.items);
  const customerName =
    (customerCode ? customerNames.get(customerCode) : null) ?? 'Unknown';

  const createdByRaw = String(row.created_by ?? '').trim();
  const createdByFallback = createdByRaw || 'Customer Portal';
  const batchesRaw = parseOrderBatches(row.batches);
  const batches = batchesRaw.map((batch) => ({
    ...batch,
    created_by: resolveDemDisplayUser(
      batch.created_by.trim() || (batch.batch_no === 0 ? createdByFallback : ''),
      userNames,
    ),
    exported_by: batch.exported_by
      ? resolveDemDisplayUser(batch.exported_by, userNames)
      : null,
  }));

  return {
    order_id: String(row.order_id ?? ''),
    customer_code: customerCode,
    customer_name: customerName,
    created_at: String(row.created_at ?? ''),
    created_at_display: parseCreatedAtDisplay(String(row.created_at ?? '')),
    delivery_date: String(row.delivery_date ?? ''),
    delivery_date_display: parseDeliveryDateDisplay(String(row.delivery_date ?? '')),
    ref_no: String(row.ref_no ?? '').trim() || '—',
    status: String(row.status ?? ''),
    remarks: String(row.remarks ?? '').trim() || '—',
    contact_person: String(row.contact_person ?? '').trim() || '—',
    contact_no: String(row.contact_no ?? '').trim() || '—',
    email: String(row.email ?? '').trim() || '—',
    total_amount: Number(row.total_amount ?? 0),
    waive_delivery: Boolean(row.waive_delivery),
    created_by: formatOrderCreatorDisplay(batchesRaw, userNames, createdByFallback),
    exported_by: formatOrderExporterDisplay(batchesRaw, userNames, ''),
    checked_at: row.checked_at ? String(row.checked_at) : null,
    checked_at_display: formatCheckedAtDisplay(row.checked_at ? String(row.checked_at) : null),
    checked_by: resolveDemDisplayUser(String(row.checked_by ?? ''), userNames),
    items,
    batches,
    has_delivery_fee: hasDeliveryFee(items),
  };
}

async function mapOrderRows(rows: Record<string, unknown>[]): Promise<DemWebOrder[]> {
  const [customerNames, userNames] = await Promise.all([
    fetchCustomerNamesByCodes(collectCustomerCodesFromRows(rows)),
    fetchDemUserDisplayNameMap(),
  ]);
  return rows.map((row) => mapOrderRow(row, customerNames, userNames));
}

export async function fetchWebOrdersPage(
  offset: number,
  limit: number,
  searchKw?: string,
): Promise<DemWebOrder[]> {
  const db = getDbSupabase('dem');

  let query = db.from('orders').select(ORDER_SELECT).eq('source', 'Web');

  const kw = searchKw?.trim();
  if (kw) {
    query = query.or(`order_id.ilike.%${kw}%,ref_no.ilike.%${kw}%,customer_code.ilike.%${kw}%`);
  } else {
    query = query.neq('status', 'Archived');
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw new Error(formatSupabaseError(error));

  return mapOrderRows((data ?? []) as Record<string, unknown>[]);
}

export function mergeWebOrders(existing: DemWebOrder[], incoming: DemWebOrder[]): DemWebOrder[] {
  const map = new Map(existing.map((order) => [order.order_id, order]));
  for (const order of incoming) {
    map.set(order.order_id, order);
  }
  return [...map.values()].sort((a, b) => b.order_id.localeCompare(a.order_id));
}

export function filterWebOrdersClient(
  orders: DemWebOrder[],
  search: string,
  statusFilter: DemOrderListFilter = 'All',
): DemWebOrder[] {
  const keywords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);

  return orders.filter((order) => {
    if (statusFilter === 'NeedsCheck' && (order.status !== 'Processed' || order.checked_at)) {
      return false;
    }
    if (keywords.length === 0) return true;

    const pool = [
      order.order_id,
      order.ref_no,
      order.customer_code,
      order.customer_name,
      order.status,
      order.created_by,
      order.exported_by,
      order.checked_by,
      order.remarks,
    ]
      .join(' ')
      .toLowerCase();
    return keywords.every((kw) => pool.includes(kw));
  });
}

export async function fetchPendingWebOrders(): Promise<DemWebOrder[]> {
  const db = getDbSupabase('dem');

  const { data, error } = await db
    .from('orders')
    .select(ORDER_SELECT)
    .eq('source', 'Web')
    .eq('status', 'Pending');

  if (error) throw new Error(formatSupabaseError(error));

  return mapOrderRows((data ?? []) as Record<string, unknown>[]);
}

/** Pending B2B orders that still carry a delivery-fee line and have not been waived. */
export function pendingWebOrdersNeedingDeliveryWaive(orders: DemWebOrder[]): DemWebOrder[] {
  return orders.filter((order) => order.has_delivery_fee && !order.waive_delivery);
}

export type CloudExportResult = {
  exported: number;
  notifications: ProcessedNotificationResult;
};

export async function exportPendingWebOrdersToCloudQueue(exportedBy: string): Promise<CloudExportResult> {
  assertDemActorReady(exportedBy);
  const orders = await fetchPendingWebOrders();
  if (orders.length === 0) {
    return { exported: 0, notifications: { sent: 0, skippedNoEmail: 0, failed: [] } };
  }

  const { hdrRows, detRows, orderIds, batchIds } = buildBatchAwareExportRows(orders, {
    skipWaivedDeliveryFee: true,
  });
  if (orderIds.length === 0 || (hdrRows.length === 0 && detRows.length === 0)) {
    return { exported: 0, notifications: { sent: 0, skippedNoEmail: 0, failed: [] } };
  }

  const batchId = `WEB_${new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14)}_${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
  const db = getDbSupabase('dem');

  const { error: queueError } = await db.from('export_queue').insert({
    batch_id: batchId,
    target_folder: 'sales_order',
    order_ids: orderIds,
    exported_by: exportedBy,
    hdr_rows: hdrRows,
    det_rows: detRows,
    status: 'pending',
  });
  if (queueError) throw new Error(formatSupabaseError(queueError));

  await finalizeOrderExport({ orderIds, batchIds, exportedBy });

  const notifications = await sendProcessedOrderNotifications(
    orders
      .filter((order) => orderIds.includes(order.order_id))
      .map((order) => ({
      order_id: order.order_id,
      customer_code: order.customer_code,
      customer_name: order.customer_name,
      ref_no: order.ref_no,
      delivery_date_display: order.delivery_date_display,
      email: order.email,
      items: order.items,
    })),
  );

  return { exported: orderIds.length, notifications };
}

export async function voidWebOrder(orderId: string, voidedBy: string): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db
    .from('orders')
    .update({
      status: 'Cancelled',
      exported_by: `${voidedBy} (Void)`,
    })
    .eq('order_id', orderId);

  if (error) throw new Error(formatSupabaseError(error));
}

export {
  markOrderChecked as markWebOrderChecked,
  unmarkOrderChecked as unmarkWebOrderChecked,
} from './demOrderChecked';

export async function waiveWebOrderDeliveryFee(orderId: string): Promise<void> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('orders').select('total_amount').eq('order_id', orderId).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Order not found.');

  const currentTotal = Number((data as Record<string, unknown>).total_amount ?? 0);
  const newTotal = Math.max(0, currentTotal - 35);

  const { error: updateError } = await db
    .from('orders')
    .update({
      waive_delivery: true,
      total_amount: newTotal,
    })
    .eq('order_id', orderId);

  if (updateError) throw new Error(formatSupabaseError(updateError));
}

export function webOrderStatusTone(status: string): 'pending' | 'uploading' | 'processed' | 'cancelled' | 'default' {
  const s = status.toLowerCase();
  if (s === 'pending') return 'pending';
  if (s === 'uploading') return 'uploading';
  if (s === 'processed') return 'processed';
  if (s === 'cancelled') return 'cancelled';
  return 'default';
}

export function webOrderStatusIcon(status: string): string {
  const s = status.toLowerCase();
  if (s === 'processed') return '✓';
  if (s === 'uploading') return '⏳';
  if (s === 'cancelled') return '✕';
  if (s === 'archived') return '🗄';
  return '🌐';
}

export const WEB_ORDERS_INITIAL_PAGE = INITIAL_PAGE;
export const WEB_ORDERS_MORE_PAGE = MORE_PAGE;
